import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Authenticated from '../../layout/Authenticated';
import SearchableInvoiceDropdown from '../../components/SearchableInvoiceDropdown';
import { Save, ArrowLeft, RotateCcw, AlertCircle, IndianRupee, FileText } from 'lucide-react';
import axiosInstance from '../../api/axiosInstance';

const NewSalesReturn = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        original_invoice_id: '',
        return_date: new Date().toISOString().split('T')[0],
        return_reason: 'OTHER',
        notes: '',
        prescription_returned: false,
        is_direct_refund: true,
        refund_amount: '',
        refund_method: 'CASH',
        refund_date: new Date().toISOString().split('T')[0]
    });

    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [returnItems, setReturnItems] = useState([]);
    const [nextId, setNextId] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success');

    const return_reasons = [
        { value: 'DEFECTIVE', label: 'Defective Product' },
        { value: 'EXPIRED', label: 'Expired Medicine' },
        { value: 'WRONG_MEDICINE', label: 'Wrong Medicine' },
        { value: 'SIDE_EFFECTS', label: 'Side Effects' },
        { value: 'DOCTOR_CHANGE', label: 'Doctor Changed Prescription' },
        { value: 'DUPLICATE_PURCHASE', label: 'Duplicate Purchase' },
        { value: 'OTHER', label: 'Other' }
    ];

    useEffect(() => {
        // No need to fetch invoices initially as SearchableInvoiceDropdown handles it
    }, []);



    const handleInvoiceSelect = (invoice) => {
        setSelectedInvoice(invoice);
        setFormData(prev => ({ ...prev, original_invoice_id: invoice ? invoice.id : '' }));

        // Initialize return items with all invoice items
        if (invoice && invoice.InvoiceItems) {
            const items = invoice.InvoiceItems.map((item, index) => ({
                id: index + 1,
                original_invoice_item_id: item.id,
                return_quantity: 0,
                return_reason: 'OTHER',
                max_quantity: item.quantity,
                invoiceItem: item
            }));
            setReturnItems(items);
            setNextId(items.length + 1);
        } else {
            setReturnItems([]);
            setNextId(1);
        }
    };

    const showAlertMessage = (message, type = 'success') => {
        setAlertMessage(message);
        setAlertType(type);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
    };


    const calculateTotal = () => {
        return returnItems.reduce((total, item) => {
            if (item.invoiceItem && item.return_quantity > 0) {
                return total + (item.return_quantity * parseFloat(item.invoiceItem.price_per_unit || item.invoiceItem.priceperunit));
            }
            return total;
        }, 0);
    };

    useEffect(() => {
        if (formData.is_direct_refund) {
            const total = calculateTotal();
            setFormData(prev => {
                const newTotal = total.toFixed(2);
                if (prev.refund_amount !== newTotal) {
                    return { ...prev, refund_amount: newTotal };
                }
                return prev;
            });
        }
    }, [returnItems, formData.is_direct_refund]);

    const updateReturnItem = (id, field, value) => {
        setReturnItems(prev => prev.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    // const calculateTotal = () => {
    //   return returnItems.reduce((total, item) => {
    //     if (item.invoiceItem && item.returnquantity > 0) {
    //       // FIXED: Use correct field name from API response
    //       return total + (item.returnquantity * parseFloat(item.invoiceItem.price_per_unit));
    //     }
    //     return total;
    //   }, 0);
    // };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.original_invoice_id) newErrors.invoice = 'Invoice selection is required';
        if (!formData.return_date) newErrors.return_date = 'Return date is required';

        const activeItems = returnItems.filter(item => Number(item.return_quantity) > 0);
        if (activeItems.length === 0) newErrors.items = 'At least one item with return quantity is required';

        returnItems.forEach((item, index) => {
            if (Number(item.return_quantity) > 0) {
                if (Number(item.return_quantity) > Number(item.max_quantity)) {
                    newErrors[`item_${index}_quantity`] = `Return quantity cannot exceed ${item.max_quantity}`;
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const activeItems = returnItems.filter(item => item.return_quantity > 0);

            const submitData = {
                ...formData,
                customer_mobile: selectedInvoice?.mobile_number || '',
                items: activeItems.map(item => ({
                    original_invoice_item_id: item.original_invoice_item_id,
                    return_quantity: parseInt(item.return_quantity),
                    return_reason: item.return_reason,
                    is_medicine_intact: item.is_medicine_intact !== undefined ? item.is_medicine_intact : true
                })),
                return_status: formData.is_direct_refund ? 'REFUNDED' : 'PENDING',
                refund_amount: formData.is_direct_refund ? parseFloat(formData.refund_amount) : 0,
                refund_method: formData.is_direct_refund ? formData.refund_method : null,
                refund_date: formData.is_direct_refund ? formData.refund_date : null
            };

            const response = await axiosInstance.post('/sales-returns', submitData);
            showAlertMessage('Sales return created successfully!', 'success');

            setTimeout(() => {
                navigate('/sales-return-list');
            }, 2000);

        } catch (error) {
            console.error('Error creating sales return:', error);
            const errorMessage = error.response?.data?.error || 'Error creating sales return';
            showAlertMessage(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Alert */}
                    {showAlert && (
                        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${alertType === 'success' ? 'bg-green-500' : 'bg-red-500'
                            } text-white max-w-md`}>
                            <div className="flex items-center justify-between">
                                <span>{alertMessage}</span>
                                <button
                                    onClick={() => setShowAlert(false)}
                                    className="text-white hover:text-gray-200 ml-4"
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => navigate('/sales-return-list')}
                                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div className="bg-teal-600 p-2 rounded-lg">
                                <RotateCcw className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Process Sales Return</h1>
                                <p className="text-sm text-gray-600 mt-1">Create a new customer return request</p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Return Information */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-900 mb-6">Return Information</h2>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Invoice Selection */}
                                <div className="lg:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Select Invoice <span className="text-red-500">*</span>
                                    </label>
                                    <SearchableInvoiceDropdown
                                        value={formData.original_invoice_id}
                                        onChange={handleInvoiceSelect}
                                        placeholder="Search by customer name, mobile, or invoice ID..."
                                        className="w-full"
                                        error={!!errors.invoice}
                                    />
                                    {errors.invoice && <p className="text-red-500 text-xs mt-1">{errors.invoice}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Return Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.return_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, return_date: e.target.value }))}
                                        className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                                    />
                                    {errors.return_date && <p className="text-red-500 text-xs mt-1">{errors.return_date}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary Return Reason</label>
                                    <select
                                        value={formData.return_reason}
                                        onChange={(e) => setFormData(prev => ({ ...prev, return_reason: e.target.value }))}
                                        className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                                    >
                                        {return_reasons.map((reason) => (
                                            <option key={reason.value} value={reason.value}>
                                                {reason.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                    <textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                        rows={3}
                                        placeholder="Additional notes about the return..."
                                        className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={formData.prescriptionreturned}
                                            onChange={(e) => setFormData(prev => ({ ...prev, prescriptionreturned: e.target.checked }))}
                                            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                        />
                                        <span className="text-gray-700">Prescription returned by customer</span>
                                    </label>
                                </div>
                            </div>
                        </div>








                        {/* Invoice Details */}
                        {selectedInvoice && (
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                <h2 className="text-lg font-semibold text-gray-900 mb-6">Invoice Details</h2>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                                    <div>
                                        <span className="text-sm text-gray-600">Customer:</span>
                                        <div className="font-medium text-gray-900">{selectedInvoice.name}</div>
                                        <div className="text-sm text-gray-500">{selectedInvoice.mobile_number}</div>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-600">Invoice Date:</span>
                                        <div className="font-medium text-gray-900">
                                            {new Date(selectedInvoice.invoice_date).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-600">Total Amount:</span>
                                        <div className="font-medium text-gray-900">
                                            ₹{parseFloat(selectedInvoice.total_amount).toFixed(2)}
                                        </div>
                                    </div>
                                </div>

                                {errors.items && <p className="text-red-500 text-sm mb-4">{errors.items}</p>}

                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medicine</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch & Expiry</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sold Qty</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Return Qty</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Return Value</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Condition</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {returnItems.map((item, index) => (
                                                <tr key={item.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-4 whitespace-nowrap">
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {item.invoiceItem.medicines?.name}
                                                            </div>
                                                            <div className="text-sm text-gray-500">
                                                                {item.invoiceItem.medicines?.generic_name}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <div>Batch: {item.invoiceItem.batch_number}</div>
                                                        <div className="text-gray-500">
                                                            Exp: {new Date(item.invoiceItem.expiry_date).toLocaleDateString()}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {item.invoiceItem.quantity} units
                                                    </td>
                                                    <td className="px-4 py-4 whitespace-nowrap">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max={item.max_quantity}
                                                            value={item.return_quantity}
                                                            onChange={(e) => updateReturnItem(item.id, 'return_quantity', e.target.value)}
                                                            className="w-20 px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                            placeholder="0"
                                                        />
                                                        {errors[`item_${index}_quantity`] && (
                                                            <p className="text-red-500 text-xs mt-1">{errors[`item_${index}_quantity`]}</p>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4 whitespace-nowrap">
                                                        <select
                                                            value={item.return_reason}
                                                            onChange={(e) => updateReturnItem(item.id, 'return_reason', e.target.value)}
                                                            className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                        >
                                                            {return_reasons.map((reason) => (
                                                                <option key={reason.value} value={reason.value}>
                                                                    {reason.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                                                        {Number(item.return_quantity) > 0 && (
                                                            <div className="font-medium text-teal-600">
                                                                ₹{(Number(item.return_quantity) * parseFloat(item.invoiceItem.price_per_unit)).toFixed(2)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4 whitespace-nowrap">
                                                        <select
                                                            value={item.is_medicine_intact !== undefined ? (item.is_medicine_intact ? 'intact' : 'damaged') : 'intact'}
                                                            onChange={(e) => updateReturnItem(item.id, 'is_medicine_intact', e.target.value === 'intact')}
                                                            className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                        >
                                                            <option value="intact">Intact (Resaleable)</option>
                                                            <option value="damaged">Damaged</option>
                                                        </select>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Total Summary */}
                                {calculateTotal() > 0 && (
                                    <div className="mt-6 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700">Total Return Value:</span>
                                            <div className="flex items-center text-lg font-bold text-teal-600">
                                                <IndianRupee size={18} className="mr-1" />
                                                ₹{calculateTotal().toFixed(2)}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Direct Refund Section */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    Direct Refund & Restock
                                </h2>
                                <label className="flex items-center cursor-pointer relative">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_direct_refund}
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            setFormData(prev => ({
                                                ...prev,
                                                is_direct_refund: isChecked,
                                                refund_amount: isChecked ? calculateTotal().toFixed(2) : ''
                                            }));
                                        }}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                                    <span className="ml-3 text-sm font-medium text-gray-900">Enable</span>
                                </label>
                            </div>

                            {formData.is_direct_refund && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fadeIn">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Refund Method <span className="text-red-500">*</span></label>
                                        <select
                                            value={formData.refund_method}
                                            onChange={(e) => setFormData(prev => ({ ...prev, refund_method: e.target.value }))}
                                            className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                                        >
                                            <option value="CASH">Cash</option>
                                            <option value="ONLINE">Online</option>
                                            <option value="CREDIT_NOTE">Credit Note</option>
                                            <option value="ADJUSTMENT">Adjustment</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Refund Amount <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.refund_amount}
                                                onChange={(e) => setFormData(prev => ({ ...prev, refund_amount: e.target.value }))}
                                                className="w-full pl-9 px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-bold text-teal-700"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Refund Date <span className="text-red-500">*</span></label>
                                        <input
                                            type="date"
                                            value={formData.refund_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, refund_date: e.target.value }))}
                                            className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                    <div className="md:col-span-3 bg-teal-50 p-3 rounded-lg border border-teal-200 text-sm text-teal-800 flex items-center gap-2">
                                        <AlertCircle className="h-4 w-4" />
                                        Stock for intact items will be restored immediately upon saving.
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex justify-start items-center space-x-3">
                            <button
                                type="submit"
                                disabled={isSubmitting || !selectedInvoice || calculateTotal() === 0}
                                className={`px-6 py-2.5 rounded-lg flex items-center ${isSubmitting || !selectedInvoice || calculateTotal() === 0
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-teal-600 text-white hover:bg-teal-700 shadow-md hover:shadow-lg'
                                    } transition-all duration-200`}
                            >
                                <Save size={18} className="mr-2" />
                                {isSubmitting ? 'Processing...' : 'Process Return'}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/sales-return-list')}
                                className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div >
        </Authenticated >
    );
};

export default NewSalesReturn;